import { Image, Upload } from "lucide-react";
import { useEffect, useState } from "react";
import { labelClass } from "@/components/admin/companyProfile/styles";
import { useToast } from "@/components/shared";
import { getApiBaseUrl } from "@/config/runtime";
import { getApiErrorMessage, getHeaders } from "@/lib/api";
import { brandService } from "@/lib/services";
import type { Brand } from "@/types";

interface LogoSectionProps {
  /** The brand being edited — logo upload needs its uuid. */
  brand: Brand;
  readOnly: boolean;
  /** Bump to force a preview refetch (e.g. after setting a logo candidate). */
  refreshKey?: number;
}

/** Logo preview + upload. Only rendered in edit mode (a logo needs an existing brand uuid). */
export function LogoSection({ brand, readOnly, refreshKey = 0 }: Readonly<LogoSectionProps>) {
  const { showToast } = useToast();
  const [logoUrl, setLogoUrl] = useState<string | null>(null);

  // Fetch the auth'd logo blob for preview. The URL is path-based and doesn't
  // change on re-upload, so `refreshKey` forces a refetch after any logo change.
  useEffect(() => {
    if (!brand.logoUrl) return;
    let revoke: string | null = null;
    let active = true;
    void (async () => {
      try {
        const headers = await getHeaders();
        const res = await fetch(`${getApiBaseUrl()}${brand.logoUrl}`, { headers });
        if (!res.ok) return;
        const blob = await res.blob();
        if (!active) return;
        revoke = URL.createObjectURL(blob);
        setLogoUrl(revoke);
      } catch {
        /* preview is best-effort */
      }
    })();
    return () => {
      active = false;
      if (revoke) URL.revokeObjectURL(revoke);
    };
  }, [brand.logoUrl, refreshKey]);

  const handleLogoChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const res = await brandService.uploadLogo(brand.uuid, file);
      const url = res.data.logoUrl;
      if (url) {
        const headers = await getHeaders();
        const blobRes = await fetch(`${getApiBaseUrl()}${url}`, { headers });
        if (blobRes.ok) setLogoUrl(URL.createObjectURL(await blobRes.blob()));
      }
      showToast("Logo uploaded", "success");
    } catch (err) {
      showToast(getApiErrorMessage(err, "Failed to upload logo"), "error");
    }
  };

  return (
    <div>
      <span className={labelClass}>
        <Image className="inline h-3.5 w-3.5 mr-1" />
        Logo
      </span>
      <div className="flex items-center gap-4">
        {logoUrl && (
          <img
            src={logoUrl}
            alt="Brand logo"
            className="h-12 w-auto max-w-[200px] object-contain rounded border border-border-secondary p-1"
          />
        )}
        {!readOnly && (
          <label className="flex items-center gap-2 px-4 py-2 bg-surface-secondary hover:bg-interactive-active text-content-primary rounded-lg text-sm font-medium cursor-pointer transition-colors border border-border-secondary">
            <Upload className="h-4 w-4" />
            {logoUrl ? "Replace logo" : "Upload logo"}
            <input
              type="file"
              accept="image/png,image/jpeg,image/svg+xml,image/webp"
              className="hidden"
              onChange={handleLogoChange}
            />
          </label>
        )}
      </div>
      <p className="mt-1.5 text-xs text-content-tertiary">PNG, JPEG, SVG, or WebP. Max 5 MB.</p>
    </div>
  );
}
